/*
 * Каталог встроенной документации ShbDeployer.
 *
 * Статьи хранятся в репозитории как docs/user/*.md и преобразуются в HTML на
 * этапе сборки (webpack/markdown-doc-loader.js), поэтому документация входит
 * в поставку приложения и открывается без обращения к внешним ресурсам.
 *
 * Здесь задаётся только структура: разделы, порядок статей и краткие описания
 * для карточек и поиска. Текст статей — в Markdown-файлах.
 */

/* eslint-disable global-require */
const ARTICLE_SOURCES = {
  'getting-started': () => require('../../../docs/user/getting-started.md'),
  installation: () => require('../../../docs/user/installation.md'),
  environments: () => require('../../../docs/user/environments.md'),
  applications: () => require('../../../docs/user/applications.md'),
  registries: () => require('../../../docs/user/registries.md'),
  users: () => require('../../../docs/user/users.md'),
  roles: () => require('../../../docs/user/roles.md'),
  settings: () => require('../../../docs/user/settings.md'),
  audit: () => require('../../../docs/user/audit.md'),
  'system-status': () => require('../../../docs/user/system-status.md'),
  diagnostics: () => require('../../../docs/user/diagnostics.md'),
  'backup-restore': () => require('../../../docs/user/backup-restore.md'),
  api: () => require('../../../docs/user/api.md'),
  support: () => require('../../../docs/user/support.md'),
  troubleshooting: () => require('../../../docs/user/troubleshooting.md'),
  glossary: () => require('../../../docs/user/glossary.md'),
};
/* eslint-enable global-require */

/**
 * Разделы содержания. Порядок статей внутри раздела определяет переходы
 * «предыдущая» и «следующая».
 */
export const DOCUMENTATION_CATEGORIES = [
  {
    id: 'start',
    title: 'Начало работы',
    icon: 'fa-rocket',
    articles: [
      {
        id: 'getting-started',
        title: 'Первый запуск и вход',
        summary: 'Создание администратора при первом открытии, вход в систему, состав главного меню.',
        keywords: 'первый запуск администратор вход пароль меню интерфейс',
      },
      {
        id: 'installation',
        title: 'Установка и запуск',
        summary: 'Запуск ShbDeployer в Docker Compose, параметры окружения, обновление версии.',
        keywords: 'установка docker compose запуск порт том обновление',
      },
    ],
  },
  {
    id: 'workloads',
    title: 'Приложения и окружения',
    icon: 'fa-cubes',
    articles: [
      {
        id: 'environments',
        title: 'Окружения',
        summary: 'Подключение окружений Docker, группы и теги, управление доступом.',
        keywords: 'окружение docker группа тег доступ сокет',
      },
      {
        id: 'applications',
        title: 'Приложения и стеки',
        summary: 'Развертывание приложений из Compose-файла, обновление и остановка стеков, работа с контейнерами.',
        keywords: 'стек приложение compose контейнер развертывание образ переменные',
      },
    ],
  },
  {
    id: 'infrastructure',
    title: 'Инфраструктура',
    icon: 'fa-plug',
    articles: [
      {
        id: 'registries',
        title: 'Реестры образов',
        summary: 'Подключение частного реестра образов и использование его при развертывании.',
        keywords: 'реестр registry образ аутентификация docker hub',
      },
    ],
  },
  {
    id: 'administration',
    title: 'Администрирование',
    icon: 'fa-users',
    articles: [
      {
        id: 'users',
        title: 'Пользователи и команды',
        summary: 'Создание пользователей, назначение роли приложения, объединение в команды.',
        keywords: 'пользователь команда администратор пароль учётная запись',
      },
      {
        id: 'roles',
        title: 'Роли и права доступа',
        summary: 'Встроенные роли окружения, назначение доступа и проверка фактических прав.',
        keywords: 'роль права доступ оператор администратор окружения',
      },
      {
        id: 'settings',
        title: 'Настройки',
        summary: 'Параметры приложения, аутентификация, SSL-сертификат, скрытые контейнеры.',
        keywords: 'настройки логотип снимки состояния ssl https шаблоны',
      },
      {
        id: 'audit',
        title: 'Журнал действий',
        summary: 'Просмотр действий пользователей, фильтрация, экспорт, идентификаторы запроса и корреляции.',
        keywords: 'журнал аудит действия фильтр экспорт csv request id correlation',
      },
    ],
  },
  {
    id: 'system',
    title: 'Система',
    icon: 'fa-heartbeat',
    articles: [
      {
        id: 'system-status',
        title: 'Состояние системы',
        summary: 'Состояние компонентов, пробы /health, /ready и /live для оркестраторов.',
        keywords: 'состояние компоненты health ready live проба оркестратор',
      },
      {
        id: 'diagnostics',
        title: 'Диагностика',
        summary: 'Сведения об установке, среде выполнения, объектах и функциях; копирование отчёта.',
        keywords: 'диагностика версия сборка память goroutines идентификатор установки',
      },
      {
        id: 'backup-restore',
        title: 'Резервное копирование и восстановление',
        summary: 'Что входит в резервную копию, как её создать и как восстановить установку.',
        keywords: 'резервная копия backup восстановление том volume данные',
      },
    ],
  },
  {
    id: 'api',
    title: 'API',
    icon: 'fa-code',
    articles: [
      {
        id: 'api',
        title: 'REST API и OpenAPI',
        summary: 'Аутентификация, ключи доступа, спецификация OpenAPI из комплекта поставки.',
        keywords: 'api rest openapi swagger токен ключ jwt',
      },
    ],
  },
  {
    id: 'help',
    title: 'Помощь',
    icon: 'fa-life-ring',
    articles: [
      {
        id: 'support',
        title: 'Техническая поддержка',
        summary: 'Как обратиться в поддержку и какие сведения приложить к обращению.',
        keywords: 'поддержка обращение диагностические сведения контакты',
      },
      {
        id: 'troubleshooting',
        title: 'Диагностика проблем',
        summary: 'Типичные неполадки: недоступность интерфейса, ошибки окружения, потеря доступа.',
        keywords: 'проблема ошибка не открывается недоступен пароль восстановление',
      },
      {
        id: 'glossary',
        title: 'Словарь терминов',
        summary: 'Термины интерфейса ShbDeployer и их соответствие понятиям Docker.',
        keywords: 'термины словарь стек окружение образ том сеть',
      },
    ],
  },
];

/** Плоский список статей в порядке содержания. */
export const DOCUMENTATION_ARTICLES = DOCUMENTATION_CATEGORIES.flatMap((category) =>
  category.articles.map((article) => ({
    ...article,
    categoryId: category.id,
    categoryTitle: category.title,
    categoryIcon: category.icon,
  }))
);

/** Возвращает описание статьи по идентификатору или undefined. */
export function findArticle(id) {
  return DOCUMENTATION_ARTICLES.find((article) => article.id === id);
}

/**
 * Загружает содержимое статьи: готовый HTML и список заголовков.
 * Возвращает null, если статьи с таким идентификатором нет.
 */
export function loadArticleContent(id) {
  const source = ARTICLE_SOURCES[id];
  return source ? source() : null;
}

/** Соседние статьи для переходов «предыдущая» и «следующая». */
export function getNeighbours(id) {
  const index = DOCUMENTATION_ARTICLES.findIndex((article) => article.id === id);
  if (index === -1) {
    return { previous: null, next: null };
  }
  return {
    previous: index > 0 ? DOCUMENTATION_ARTICLES[index - 1] : null,
    next: index < DOCUMENTATION_ARTICLES.length - 1 ? DOCUMENTATION_ARTICLES[index + 1] : null,
  };
}

/**
 * Поиск по содержанию: сопоставляются название статьи, краткое описание,
 * ключевые слова и название раздела. Поиск выполняется на стороне браузера
 * по данным из сборки — внешние службы не используются.
 */
export function filterCategories(query) {
  const needle = String(query || '')
    .trim()
    .toLowerCase();
  if (!needle) {
    return DOCUMENTATION_CATEGORIES;
  }
  const words = needle.split(/\s+/);
  const matches = (article, categoryTitle) => {
    const haystack = `${article.title} ${article.summary} ${article.keywords} ${categoryTitle}`.toLowerCase();
    return words.every((word) => haystack.includes(word));
  };

  return DOCUMENTATION_CATEGORIES.map((category) => ({
    ...category,
    articles: category.articles.filter((article) => matches(article, category.title)),
  })).filter((category) => category.articles.length > 0);
}
